import { parseDate } from '../helpers/parse-date';
import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';

const expenseItems = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported record. Imported row will be checked for duplicate. If a duplicate is found, the row will be skipped from import.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '';
    },
  },
  Date: {
    requirementType: 'required',
    description: 'Date of item being imported in "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => {
      return !!parseDate(value);
    },
  },
  Category: {
    requirementType: 'required',
    description: 'Expense category of the expense item.',
    dataType: 'string',
    helpUrl: 'https://help.ruddr.io/hc/en-us/articles/360055882494-Manage-Expense-Categories',
    validator: () => true,
  },
  'Member Email': {
    requirementType: 'required',
    description: 'Provide the email address associated with the workspace member.',
    dataType: 'email',
    validator: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
  },

  Status: {
    requirementType: 'optional',
    description:
      'Provide a value of "approved", "not_submitted" or "rejected" for the Status. "approved" will be used by default.',
    dataType: 'string',
    validator: (value) => ['approved', 'not_submitted', 'rejected'].includes(value.trim().toLowerCase()),
  },

  'Unit Amount': {
    requirementType: 'optional',
    description: 'Unit Amount of the expense item; used only for certain expense categories.',
    dataType: 'number',
    validator: (value) => {
      if (value === undefined || value === null || value === '') return true; // optional
      const num = Number(value);
      return Number.isFinite(num) && num >= 0;
    },
  },

  'Unit Count': {
    requirementType: 'optional',
    description: 'Count of units; used with Unit Amount for categories with calculated expenses (e.g., mileage).',
    dataType: 'number',
    validator: (value) => {
      if (value === undefined || value === null || value === '') return true; // optional
      const num = Number(value);
      return Number.isFinite(num) && num >= 0;
    },
  },
  Amount: {
    requirementType: 'required',
    description:
      'Amount of the expense item in the currency provided. If Unit Count and Unit Amount are provided, the Amount must equal Unit Count × Unit Amount.',
    dataType: 'number',
    validator: (value, row) => {
      // Required check
      if (value === undefined || value === null || value === '') return false;

      const amount = Number(value);
      if (!Number.isFinite(amount) || amount < 0) return false;

      // Extract related optional fields
      const unitCountRaw = row['Unit Count'];
      const unitAmountRaw = row['Unit Amount'];

      // If either Unit Count or Unit Amount is missing, skip the comparison check
      if (unitCountRaw === undefined || unitAmountRaw === undefined || unitCountRaw === '' || unitAmountRaw === '') {
        return true;
      }

      const unitCount = Number(unitCountRaw);
      const unitAmount = Number(unitAmountRaw);

      // Validate those fields are usable numbers
      if (!Number.isFinite(unitCount) || unitCount < 0) return true;
      if (!Number.isFinite(unitAmount) || unitAmount < 0) return true;

      // Compare calculated amount with tolerance to handle floating point precision
      const calculated = unitCount * unitAmount;
      const tolerance = 0.000001;
      return Math.abs(amount - calculated) <= tolerance;
    },
  },
  Currency: {
    requirementType: 'optional',
    description:
      'Provide a valid 3-letter ISO currency code (for example: USD, EUR, GBP, AUD, BRL). Leave blank to use the workspace default currency.',
    dataType: 'string',
    validator: (value) => {
      if (!value) return false;
      const cleaned = value.trim().toUpperCase();
      return /^[A-Z]{3}$/.test(cleaned);
    },
  },
  'Vendor Name': {
    requirementType: 'optional',
    description: 'Vendor related to the expense item with whom the transaction took place.',
    dataType: 'string',
    validator: () => true,
  },
  'Client Name': {
    requirementType: 'conditional',
    description:
      'The Name of the client associated with this expense item. This field must match the Name of a Client exactly, including spaces, punctuation and capitalization, otherwise the row may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  'Project Name': {
    requirementType: 'conditional',
    description:
      'The name of the project associated with the expense item. This field must match the name of a project exactly, including spaces, punctuation and capitalization, otherwise the row may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  Billable: {
    requirementType: 'optional',
    description: '"Yes" if you consider this item billable to the client.',
    dataType: 'boolean',
    validator: (value) => {
      return value === undefined || isTruthy(value) || isFalsy(value);
    },
  },
  Reimbursable: {
    requirementType: 'optional',
    description:
      'Provide "Yes" if you consider the item on the invoice. Otherwise will be considered not reimbursable.',
    dataType: 'boolean',
    validator: (value) => {
      return !value || isTruthy(value) || isFalsy(value);
    },
  },
  Reimbursed: {
    requirementType: 'optional',
    description:
      'Provide "Yes" if you consider the expense item as having been reimbused. Otherwise will be considered not reimbursed.',
    dataType: 'boolean',
    validator: (value) => {
      return !value || isTruthy(value) || isFalsy(value);
    },
  },
  Notes: {
    requirementType: 'optional',
    description: 'Notes on an expense item where details could be provided.',
    dataType: 'string',
    validator: () => true,
  },
};

export default expenseItems;
